/**
 * Display formatting for the jobs list and job page (D-060). Money and hours
 * are `DecimalString` — exact text from Postgres — and this module only ever
 * slices and groups that text; it never runs it through `Number()` or
 * `parseFloat` (integrity rule 3: money is never a float, and hours follow
 * the same rule here so a long shift never rounds off in a table cell).
 */

import type { DecimalString, JobStatusTab } from '@unitedos/shared';

/**
 * Thousands separators on the integer part. The fraction is kept exactly as
 * given, padded with zeros to `minFraction` places — padding is lossless;
 * nothing is ever cut or rounded.
 */
function withThousands(raw: DecimalString, minFraction = 0): string {
  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const dot = unsigned.indexOf('.');
  const intPart = dot < 0 ? unsigned : unsigned.slice(0, dot);
  const fracPart = (dot < 0 ? '' : unsigned.slice(dot + 1)).padEnd(minFraction, '0');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${fracPart ? `.${fracPart}` : ''}`;
}

/**
 * Exactly two places, **half away from zero** — the one rounding rule a dollar amount meets on its way
 * to a screen (integrity rule 3: stated at the boundary where it happens). Integer arithmetic on the
 * text's digits in BigInt, never a float: "12.3456" → "12.35", "-12.345" → "-12.35", "0.995" → "1.00",
 * "-0.004" → "0.00" (no minus on a zero). Display only — the response text stays Postgres's.
 */
function toTwoPlaces(raw: DecimalString): string {
  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const dot = unsigned.indexOf('.');
  const intPart = (dot < 0 ? unsigned : unsigned.slice(0, dot)) || '0';
  const fracPart = dot < 0 ? '' : unsigned.slice(dot + 1);
  let cents = BigInt(intPart + fracPart.slice(0, 2).padEnd(2, '0'));
  if (fracPart.length > 2 && fracPart.charCodeAt(2) >= 53 /* '5' */) cents += 1n;
  const digits = cents.toString().padStart(3, '0');
  const text = `${digits.slice(0, -2)}.${digits.slice(-2)}`;
  return negative && cents !== 0n ? `-${text}` : text;
}

/**
 * "$14,860.00" — grouped, always exactly 2 places ("7430" → "$7,430.00"; "12.3456" → "$12.35",
 * `toTwoPlaces`, Dakota 2026-10-06 — a dollar amount never shows a third place), the dollar sign
 * ahead of the digits and after a minus ("-$1,234.56"; Dakota, 2026-10-05). The sign is the display
 * convention for the customer's one currency; the currency code still travels on every response and
 * the column header names it. `null` reads as an em dash.
 */
export function formatMoney(value: DecimalString | null): string {
  if (value === null) return '—';
  const grouped = withThousands(toTwoPlaces(value), 2);
  return grouped.startsWith('-') ? `-$${grouped.slice(1)}` : `$${grouped}`;
}

/** "38.5" — same treatment as money; a hand-entered shift length is exact text too. */
export function formatHours(value: DecimalString | null): string {
  return value === null ? '—' : withThousands(value);
}

/** Jobber's profit percentage exactly as sent, grouped, with a % sign: "40.02" → "40.02%". Never cut or rounded. */
export function formatPercent(value: DecimalString | null): string {
  return value === null ? '—' : `${withThousands(value)}%`;
}

export const JOB_STATUS_TAB_LABELS: Readonly<Record<JobStatusTab, string>> = {
  all: 'All',
  active: 'Active',
  requires_invoicing: 'Requires invoicing',
  late: 'Late',
  unscheduled: 'Unscheduled',
  on_hold: 'On hold',
  archived: 'Archived',
};

export type ChipTone = 'good' | 'warn' | 'bad' | 'accent' | 'muted';

/** `status status--<tone>`, the chip class from the style guide. */
export function chipClass(tone: ChipTone): string {
  return `status status--${tone}`;
}

/**
 * A job's status chip, from Jobber's JobStatusTypeEnum (the schema snapshot,
 * 2026-05-12). An unknown value is neutral, never guessed.
 */
export function jobStatusTone(status: string | null): ChipTone {
  switch (status) {
    case 'active':
    case 'today':
      return 'good';
    case 'upcoming':
    case 'action_required':
    case 'expiring_within_30_days':
      return 'accent';
    case 'requires_invoicing':
    case 'unscheduled':
    case 'on_hold':
      return 'warn';
    case 'late':
      return 'bad';
    default:
      return 'muted';
  }
}

/** The job page's schedule tab: Jobber's own visit-status vocabulary, given exactly. */
export type VisitStatus = 'COMPLETED' | 'UPCOMING' | 'LATE' | 'TODAY' | 'UNSCHEDULED' | 'ACTIVE';

export function visitStatusTone(status: string | null): ChipTone {
  switch (status) {
    case 'COMPLETED':
      return 'muted';
    case 'UPCOMING':
      return 'accent';
    case 'LATE':
      return 'bad';
    case 'TODAY':
      return 'good';
    case 'UNSCHEDULED':
      return 'warn';
    case 'ACTIVE':
      return 'good';
    default:
      return 'muted';
  }
}

/** "Completed", "Unscheduled", … — title case of the raw status, or an em dash. */
/** A Jobber status enum as a person reads it: "requires_invoicing" → "Requires invoicing", "COMPLETED" → "Completed". */
export function statusLabel(status: string | null): string {
  if (!status) return '—';
  const words = status.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const visitStatusLabel = statusLabel;

/** An invoice's status chip. */
export function invoiceStatusTone(status: string | null): ChipTone {
  // Jobber's InvoiceStatusTypeEnum (the schema snapshot, 2026-05-12).
  switch (status) {
    case 'paid':
      return 'good';
    case 'awaiting_payment':
    case 'sent_not_due':
      return 'accent';
    case 'past_due':
    case 'bad_debt':
      return 'bad';
    default:
      return 'muted'; // draft, voided, unknown
  }
}
